import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AlertService, ButtonColors, LayerService } from '@ed---interne/ng-uui-components';
import { emailValidator } from 'src/app/shared/validators/email.validator';
import { GrdfAccessRightModel, GrdfAccessRightProofType } from '../../../grdf.models';
import { CreateAccessRight } from '../../../grdf.state';
import { Observable } from 'rxjs';
import { AuthState } from 'src/app/core/states/auth.state';
import { User } from 'src/app/core/models/user.model';

@UntilDestroy()
@Component({
    selector: 'grdf-access-right-create',
    templateUrl: './grdf-access-right-create.component.html',
    styleUrls: ['./grdf-access-right-create.component.scss'],
})
export class GrdfAccessRightCreateComponent {
    public buttonColors = ButtonColors;

    public header: any;

    public accessRightFormGroup: FormGroup;

    public loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    public loggedUser: User | undefined;

    public consentMethodOptions = [
        {
            value: 'MAIL',
            label: 'Par email auprès du représentant légal',
        },
        { value: 'FILE', label: `Upload d'un fichier` },
    ];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly layerService: LayerService,
        private readonly store: Store,
        private readonly alertService: AlertService,
    ) {
        const now = new Date();
        const twoYearsAgo = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
        const fiveYearFromNow = new Date(now.getFullYear() + 5, now.getMonth(), now.getDate());
        this.accessRightFormGroup = this.formBuilder.group({
            clientName: [{ value: '' }],
            clientSirenSiret: [{ value: '' }],
            clientRepresentativeName: [{ value: '' }],
            clientRepresentativeEmail: [{ value: '', disabled: false }, [emailValidator()]],
            pdlNumber: [{ value: '' }],
            pdlPostalCode: [{ value: '' }],
            consentEnd: [{ value: fiveYearFromNow }],
            consumptionAccessStart: [{ value: twoYearsAgo }],
            clientRepresentativePhoneNumber: [{ value: '' }],
            comments: [{ value: '' }],
            consentMethod: [{ value: undefined }, Validators.required],
            fileProof: [{ value: undefined }],
        });

        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.loggedUser = data;
        });
        // this.accessRightFormGroup.get('consentMethod')?.valueChanges.subscribe((method) => {
        //     if (method === 'FILE') {
        //         fileControl?.setValidators([Validators.required]);
        //     } else {
        //         fileControl?.clearValidators();
        //         fileControl?.setValue(undefined);
        //     }
        //     fileControl?.updateValueAndValidity();
        // });

        const consentMethodControl = this.accessRightFormGroup.get('consentMethod');
        const nameControl = this.accessRightFormGroup.get('clientRepresentativeName');
        const emailControl = this.accessRightFormGroup.get('clientRepresentativeEmail');
        const phoneNumberControl = this.accessRightFormGroup.get('clientRepresentativePhoneNumber');
        const fileControl = this.accessRightFormGroup.get('fileProof');

        consentMethodControl?.valueChanges.subscribe((method: string) => {
            if (method === 'MAIL') {
                nameControl?.setValidators([Validators.required]);
                emailControl?.setValidators([Validators.required, emailValidator()]);
                phoneNumberControl?.setValidators([Validators.required]);

                fileControl?.clearValidators();
                fileControl?.setValue(undefined);
            } else {
                nameControl?.clearValidators();
                emailControl?.clearValidators();
                phoneNumberControl?.clearValidators();

                fileControl?.setValidators([Validators.required]);
            }
            nameControl?.updateValueAndValidity();
            emailControl?.updateValueAndValidity();
            phoneNumberControl?.updateValueAndValidity();

            fileControl?.updateValueAndValidity();
        });
    }

    public updateConsumptionAccessStart(date: Date) {
        this.accessRightFormGroup.patchValue({
            consumptionAccessStart: date,
        });
    }

    public updateConsentEnd(date: Date) {
        this.accessRightFormGroup.patchValue({
            consentEnd: date,
        });
    }

    async onFileUpload(file: File) {
        this.accessRightFormGroup.get('fileProof')?.setValue(file);
        this.accessRightFormGroup.get('fileProof')?.markAsDirty();
    }

    public valid(): void {
        const accessRightToCreate: Partial<GrdfAccessRightModel> = {
            clientName: this.accessRightFormGroup.value.clientName as string,
            clientSirenSiret: this.accessRightFormGroup.value.clientSirenSiret as string,
            clientRepresentativeName: this.accessRightFormGroup.value
                .clientRepresentativeName as string,
            clientRepresentativeEmail: this.accessRightFormGroup.value
                .clientRepresentativeEmail as string,
            pdlNumber: this.accessRightFormGroup.value.pdlNumber as string,
            consentEnd: this.accessRightFormGroup.value.consentEnd as Date,
            consumptionAccessStart: this.accessRightFormGroup.value.consumptionAccessStart as Date,
            consentStart: new Date() as Date,
            clientRepresentativePhoneNumber: this.accessRightFormGroup.value
                .clientRepresentativePhoneNumber as string,
            comments: this.accessRightFormGroup.value.comments as string,
            pdlPostalCode: this.accessRightFormGroup.value.pdlPostalCode as string,
            proofType: this.accessRightFormGroup.value.consentMethod as GrdfAccessRightProofType,
        };

        this.store
            .dispatch(
                new CreateAccessRight({
                    accessRight: accessRightToCreate,
                    fileProof: this.accessRightFormGroup.value.fileProof,
                }),
            )
            .subscribe({
                next: () => {
                    this.alertService.valid("Nouveau droit d'accès", "Droit d'accès créé !");
                    this.close();
                },
                error: (err) => {
                    console.info(err.message);
                    this.alertService.error('Création', err?.message ?? 'Une erreur est survenue');
                },
            });
    }

    isMail(input: string): boolean {
        const expression: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return expression.test(input);
    }

    public set params(params: { header: any }) {
        this.header = params.header;

        const now = new Date();
        const twoYearsAgo = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
        const fiveYearFromNow = new Date(now.getFullYear() + 5, now.getMonth(), now.getDate());

        this.accessRightFormGroup.patchValue({
            clientName: '',
            clientSirenSiret: '',
            clientRepresentativeName: '',
            clientRepresentativeEmail: '',
            pdlNumber: '',
            pdlPostalCode: '',
            consentEnd: fiveYearFromNow,
            consumptionAccessStart: twoYearsAgo,
            clientRepresentativePhoneNumber: '',
            comments: '',
            consentMethod: '',
            fileProof: null,
        });
    }

    public close(): void {
        this.layerService.close();
    }

    public canCreateEdit(): boolean {
        return this.accessRightFormGroup.invalid || !this.accessRightFormGroup.dirty;
    }

    public showPopupOnLayerClose(): boolean {
        return this.accessRightFormGroup.dirty;
    }
}
